package net.frozenbit.games.soundgame.audio

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.cancelAndJoin
import kotlinx.coroutines.launch
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlin.time.TimeSource
import soundgame.app.shared.generated.resources.Res
import kotlin.time.ComparableTimeMark

class AudioManager(
    private val audioPlayer: AudioPlayer,
    private val musicManager: MusicManager,
    private val scope: CoroutineScope,
    private val cacheSize: Int = 8,
    private val timeSource: TimeSource.WithComparableMarks = TimeSource.Monotonic,
) {
    private val cacheMutex = Mutex()
    private val soundCache = mutableMapOf<String, CachedSound>()
    private val playbackQueue = Channel<SoundRequest>(Channel.UNLIMITED)

    private val soundPlaybackJob = scope.launch {
        for (request in playbackQueue) {
            if (request.duck) {
                musicManager.duck()
            }
            playSound(request)
            if (request.duck) {
                musicManager.unduck()
            }
        }
    }

    /**
     * Enqueues a sound effect to be played after other sounds in the queue.
     *
     * @param duckMusic If true, the music volume will be temporarily reduced while the sound effect is playing.
     */
    fun enqueueSoundEffects(
        fileNames: List<String>,
        volume: Float = 1.0f,
        duckMusic: Boolean = false,
    ) {
        scope.launch {
            val sounds = fileNames.map { loadSound(it) }
            playbackQueue.send(SoundRequest(sounds, volume, duckMusic))
        }
    }

    /**
     * Like [enqueueSoundEffects], but plays the sound effect immediately, potentially
     * playing over other sounds in the queue.
     */
    suspend fun playSoundEffects(
        fileNames: List<String>,
        volume: Float = 1.0f,
        duckMusic: Boolean = false,
    ) {
        val sounds = fileNames.map { loadSound(it) }
        val soundRequest = SoundRequest(sounds, volume, duckMusic)
        playSound(soundRequest)
    }

    suspend fun playSoundEffect(fileName: String, volume: Float = 1.0f, duckMusic: Boolean = false) {
        playSoundEffects(listOf(fileName), volume, duckMusic)
    }

    private suspend fun loadSound(fileName: String): CachedSound = cacheMutex.withLock {
        val sound = soundCache.getOrPut(fileName) {
            CachedSound(createLoadJob(fileName), timeSource.markNow())
        }
        sound.lastRequestedAt = timeSource.markNow()
        sound.requests++
        if (soundCache.size > cacheSize) {
            val leastRecentlyUsed =
            // Find the least recently used sound that is not
                // currently enqueued
                soundCache.entries
                    .filter { it.value.plays >= it.value.requests }
                    .minByOrNull { it.value.lastRequestedAt }
            leastRecentlyUsed?.let { (key, cachedSound) ->
                onEvict(cachedSound)
                soundCache.remove(key)
                println("[debug] Evicted sound: $key")
            }
        }
        return sound
    }

    suspend fun close() {
        soundPlaybackJob.cancelAndJoin()
        cacheMutex.withLock {
            for (cachedSound in soundCache.values) {
                onEvict(cachedSound)
            }
            soundCache.clear()
        }
    }

    private suspend fun onEvict(cachedSound: CachedSound) {
        if (cachedSound.loadJob.isActive) {
            cachedSound.loadJob.cancel()
        } else if (cachedSound.loadJob.getCompletionExceptionOrNull() == null) {
            audioPlayer.release(cachedSound.loadJob.getCompleted())
        }
    }

    private suspend fun playSound(request: SoundRequest) {
        request.sounds.map { it.loadJob }.awaitAll()
        for (sound in request.sounds) {
            audioPlayer.play(sound.loadJob.await(), request.volume)
            sound.plays++
        }
    }

    private fun createLoadJob(fileName: String): Deferred<AudioHandle> =
        scope.async {
            audioPlayer.prepare(Res.readBytes("files/${fileName}"))
                .also { println("[debug] Loaded sound: $fileName") }
        }

    private data class SoundRequest(
        val sounds: List<CachedSound>,
        val volume: Float,
        val duck: Boolean,
    )

    private class CachedSound(
        val loadJob: Deferred<AudioHandle>,
        var lastRequestedAt: ComparableTimeMark,
        var requests: Int = 0,
        var plays: Int = 0,
    )
}
